﻿import * as Session from '../sessionService/session.service';
import {ILogService} from '../logservice/log.service';
import { Roles } from "../../../models/constants/authentication.constants";
import { IAuthenticationService } from '../../../authentication/authentication.service';

export interface IAuthorizationService {
    /**
     * @function Use this method to determine whether the current user would be authorized to access the given IClaimsProfile
     * @param claimsProfile 
     * @returns true if the user has access, otherwise returns false
     */
    isStateAuthorized(state: string | angular.ui.IState): boolean;

    /**
     * @function Call this method from the module.run function to activate the authorization service.
     * @returns {} 
     */
    useAuthorizationService(): void;

}

export interface IAuthorizationServiceProvider extends angular.IServiceProvider {

    /**
     * @function the claimsProfile to use if no profile is associated with the view if undefined, an error will be thrown when no claims are associated
     */
    setDefaultClaimsProfile(claimsProfile: Session.IClaimsProfile): void;
}

export class AuthorizationProvider implements IAuthorizationServiceProvider {

    private sessionService: Session.ICustomerSession;
    private $rootScope: angular.IRootScopeService;
    private logService: ILogService;
    private $stateService: angular.ui.IStateService;
    private defaultClaimsProfile: Session.IClaimsProfile;
    private $q: ng.IQService;
    private $timeout: ng.ITimeoutService;
    private authenticationService: IAuthenticationService;

    constructor() {
        this.$get.$inject = ['$q', '$state', '$rootScope', 'sessionService', 'logService', '$timeout', 'authenticationService'];
    }

    public setDefaultClaimsProfile(claimsProfile: Session.IClaimsProfile) {
        this.defaultClaimsProfile = claimsProfile;
    }

    public $get(
        $q: ng.IQService,
        $stateService: angular.ui.IStateService,
        $rootScope: angular.IRootScopeService,
        session: Session.ICustomerSession,
        log: ILogService,
        $timeout: ng.ITimeoutService,
        authenticationService: IAuthenticationService
    ): IAuthorizationService {
        this.$rootScope = $rootScope;
        this.sessionService = session;
        this.logService = log;
        this.$stateService = $stateService;
        this.$q = $q;
        this.$timeout = $timeout;
        this.authenticationService = authenticationService;
        return {
            isStateAuthorized: (state: string | angular.ui.IState) => { return this.isStateAuthorized(state); },
            useAuthorizationService: () => { this.useAuthorizationService(); }
        };
    }

    private isStateAuthorized(state: string | angular.ui.IState) {
        const workState: angular.ui.IState = (typeof state === 'string') ? this.$stateService.get(state) : state;

        // intent:  if there is a claims profile associated with the state, use it.  
        // Otherwise, try to use the default profile.  If there is no default profile, it's a development error.  Throw.
        let claimsProfile: Session.IClaimsProfile = this.unwrapClaimsProfile(workState.data);
        if (!angular.isDefined(claimsProfile)) {
            if (angular.isDefined(this.defaultClaimsProfile)) {
                claimsProfile = this.defaultClaimsProfile;
            }
            else {
                throw new Error(`There is no claimsProfile associated with this state, and no default profile is defined.  State: ${workState.name}`);
            }
        }

        if (claimsProfile.allowAnonymousAccess) {
            return true;
        }

        if (!this.authenticationService.isAuthenticated()) {
            return false;
        }

        if (!angular.isDefined(claimsProfile.claims) || claimsProfile.claims.length === 0) {
            return true;
        }
        else {
            // if the profile requires claims, verify the user possesses them
            return this.authenticationService.isInRole(claimsProfile.claims);
        }
    }

    private unwrapClaimsProfile(data: any): Session.IClaimsProfile {
        if (!angular.isDefined(data)) {
            return undefined;
        }

        if (Session.isIExposeClaimsProfile(data)) {
            return data.claimsProfile;
        }

        if (Session.isIClaimsProfile(data)) {
            return data;
        }

        return undefined;
    }

    private authorize = (e, toState, toParams, fromState, fromParams): void => {
            
        toState.resolve.authorizePromise = () => {

            const deferred = this.$q.defer();

            if (this.skipStateAuthorizationPredicate(toState)) {
                deferred.resolve(true);
                return deferred.promise;
            }        

            if (!this.authenticationService.isAuthenticated()){
                if (angular.isDefined(toState.data) && angular.isDefined(toState.data.redirectToSignOn)){
                    this.$timeout(() => {                
                        this.$stateService.transitionTo(toState.data.redirectToSignOn, {appState: toState.name }, {reload: true});
                    }, 100);
                    
                    deferred.reject();
                    return deferred.promise;               
                }
            }

                   
            try {
                if (!this.isStateAuthorized(toState)) {
                    this.logService
                        .warning(`User is not authorized.  Denying navigation to ${toState.name} (url: ${toState.url} for user:`,
                        this.sessionService.principal);

                    if (angular.isDefined(toState.data) && angular.isDefined(toState.data.redirectToSignOn)){
                        this.logService.debug('Redirecting to Sign On');
                        e.preventDefault();
                        this.$stateService.go(toState.data.redirectToSignOn);
                    }

                    deferred.reject();
                }
            }
            catch (error) {
                e.preventDefault();
                throw (error);
            }

            deferred.resolve(true);
            return deferred.promise;
        };
    }

    private isCustomerAgreementRequired(): boolean {
        // rules:
        // Customers who have not completed the customer agreements screen must be redirected to the customer agreements view.
        // exceptions:  
        //   Users who are not logged in are exempt (don't block anonymous sections of the site)
        //   Users who are performing initial sign-up are exempt (provisional sessions)
        //   Intrepid employees who are acting as internal system operators are exempt
        return !this.sessionService.isCustomerAgreementAccepted &&
            this.authenticationService.isAuthenticated() &&
            !this.sessionService.isProvisionalSession &&
            this.sessionService.hasClaim(Roles.Employee) === false;
    }

    private skipStateAuthorizationPredicate(workState: angular.ui.IState): boolean{

        let claimsProfile: Session.IClaimsProfile = this.unwrapClaimsProfile(workState.data);

        if (!angular.isDefined(claimsProfile))
            claimsProfile = this.defaultClaimsProfile;

        if (angular.isDefined(claimsProfile)) {

            //have to skip the authorization on the "Frames" because the signin badge will never load, thus never firing the badge.onload event, therefore you will just hang
            if (claimsProfile.allowAnonymousAccess) {return true; }
        }

        return false;
    }

    private useAuthorizationService(): void {
        this.$rootScope.$on('$stateChangeStart', this.authorize);

        
    }
}